import { observer } from "mobx-react";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Box, VBox } from "@/components-v2/Box";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { Button } from "@/components-v2/Button";
import { useRootStore } from "@/stores/RootStoreContext";
import { Language } from "./languages";
import { DCEvent, navigateTo, thunk, updateStore } from "@/events/eventsDomain";
import { useEffect } from "react";
import { aiApi } from "@/ai/AIAssistant";
import { Checkbox } from "@/components-v2/Checkbox";
import { concat, omit, trim } from "lodash";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { TranslationAdminState } from "./i18nDomain";
import { Dialog } from "@/components-v2/Dialog";
import { TextInput } from "@/components-v2/TextInput";
import { isBlank } from "@/utils/strings";
import { when } from "@/utils/functions";
import { singleValue } from "@/utils/queryParams";

export const TranslationAdminPage = observer(() => {
  const { translationAdmin: state, i18n, dispatch, queryParams } = useRootStore();
  const keyParam = singleValue(queryParams.key);
  const key = keyParam && keyParam in i18n.bundles.en ? keyParam : undefined;
  const add = singleValue(queryParams.add);

  const update = <K extends keyof TranslationAdminState>(
    key: K,
    val:
      | TranslationAdminState[K]
      | ((cur: TranslationAdminState[K] | undefined) => TranslationAdminState[K]),
  ): DCEvent =>
    updateStore({
      translationAdmin: (cur) => ({
        ...cur,
        [key]: typeof val === "function" ? val(cur?.[key]) : val,
      }),
    });

  const containsTranslation = (key: string) =>
    new Set(Object.keys(i18n.bundles.en)).has(key.trim());

  const selectTranslation = (key: string): DCEvent =>
    navigateTo({
      name: "translation-admin",
      queryParams: { ...queryParams, key },
    });

  const storeTranslation = (lang: Language, value: string): DCEvent[] =>
    key
      ? [
          update(lang, value),
          thunk(() => import.meta.hot?.send("i18n/store-translation", { lang, key, value })),
        ]
      : [];

  const deleteTranslation = (): DCEvent[] =>
    key ? [thunk(() => import.meta.hot?.send("i18n/delete-translation", { key }))] : [];

  const addTranslation = (key: string): DCEvent[] => [
    selectTranslation(key),
    thunk(() => import.meta.hot?.send("i18n/add-translation", { key })),
  ];

  const renameTranslation = (key?: string, newKey?: string): DCEvent[] =>
    key && newKey
      ? [
          thunk(() => import.meta.hot?.send("i18n/rename-translation", { key, newKey })),
          selectTranslation(newKey),
        ]
      : [];

  const setTranslating = (lang: string, val: boolean) =>
    update("translating", (cur) => ({ ...cur, [lang]: val }));

  const translate = (inputText: string | undefined, from: Language, to: Language): DCEvent[] => {
    if (!inputText) {
      return [];
    }
    return [
      setTranslating(to, true),
      aiApi.translateMessage({
        inputText,
        from,
        to,
        onSuccess: (res) => [setTranslating(to, false), ...storeTranslation(to, res.data)],
        onFailure: () => [setTranslating(to, false)],
      }),
    ];
  };

  useEffect(() => {
    dispatch([
      updateStore({
        translationAdmin: (cur) => ({
          ...cur,
          en: key ? i18n.bundles.en[key] : "",
          no: key ? i18n.bundles.no[key] : "",
          sv: key ? i18n.bundles.sv[key] : "",
          auto: localStorage.getItem("translation-admin.auto") === "true",
        }),
      }),
    ]);
  }, [dispatch, key, i18n]);

  useEffect(() => {
    if (add) {
      dispatch([
        update("addDialog", { input: add }),
        navigateTo(
          {
            name: "translation-admin",
            queryParams: omit(queryParams, "add"),
          },
          { replace: true },
        ),
      ]);
    }
  }, [dispatch, add, queryParams]);

  return (
    <>
      <FullScreenLayout
        leftPanelContainerProps={{ className: "basis-1/3" }}
        leftPanel={
          <VBox grow padding bg={"base"}>
            <select
              size={2}
              className="grow border rounded-sm"
              onChange={(event) => dispatch([selectTranslation(event.target.value)])}
              value={key || undefined}
            >
              {Object.keys(i18n.bundles[Language.EN])
                .toSorted()
                .map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
            </select>
          </VBox>
        }
        centerPanelContainerProps={{ className: "basis-2/3" }}
        centerPanel={
          <VBox gap={"md"}>
            <Box borderBottom padding justifyContent="space-between">
              <Button
                variant="secondary"
                label={"Add"}
                icon={"plus"}
                onClick={[update("addDialog", { input: "" })]}
              />
              {key && (
                <Box>
                  <Button
                    variant="secondary"
                    label={"Rename"}
                    icon={"edit"}
                    onClick={[update("renameDialog", { input: key })]}
                  />
                  <Button
                    variant="secondary"
                    label={"Delete"}
                    icon={"trash"}
                    color="error"
                    onClick={deleteTranslation()}
                  />
                </Box>
              )}
            </Box>
            <VBox paddingX>
              <Box>
                <label id="label-en">EN</label>
                <Checkbox
                  className="ml-md"
                  label={"Auto"}
                  checked={state?.auto ?? false}
                  onChange={(event) =>
                    dispatch([
                      update("auto", event.target.checked),
                      {
                        type: "update-local-storage",
                        key: "translation-admin.auto",
                        val: event.target.checked,
                      },
                    ])
                  }
                />
                <Button
                  variant="basic"
                  size="xs"
                  label={"-> NO"}
                  onClick={translate(state?.en ?? "", Language.EN, Language.NO)}
                  disabled={!key}
                />
                <Button
                  variant="basic"
                  size="xs"
                  label={"-> SV"}
                  onClick={translate(state?.en ?? "", Language.EN, Language.SV)}
                  disabled={!key}
                />
                {state?.translating?.en && <LoadingIndicator size="sm" label="Translating" />}
              </Box>
              <MarkdownEditor
                labelId={"label-en"}
                value={state?.en ?? ""}
                readOnly={!key}
                onChange={(value) =>
                  dispatch(
                    concat(
                      storeTranslation(Language.EN, value),
                      state?.auto
                        ? [
                            ...translate(value, Language.EN, Language.NO),
                            ...translate(value, Language.EN, Language.SV),
                          ]
                        : [],
                    ),
                  )
                }
              />
            </VBox>
            <VBox paddingX>
              <Box>
                <label id="label-no">NO</label>
                <Button
                  variant="basic"
                  size="xs"
                  label={"-> EN"}
                  onClick={translate(state?.no ?? "", Language.NO, Language.EN)}
                  disabled={!key}
                />
                <Button
                  variant="basic"
                  size="xs"
                  label={"-> SV"}
                  onClick={translate(state?.no ?? "", Language.NO, Language.SV)}
                  disabled={!key}
                />
                {state?.translating?.no && <LoadingIndicator size="sm" label="Translating" />}
              </Box>
              <MarkdownEditor
                labelId={"label-no"}
                value={state?.no ?? ""}
                readOnly={!key}
                onChange={(value) => dispatch(storeTranslation(Language.NO, value))}
              />
            </VBox>
            <VBox paddingX>
              <Box>
                <label id="label-sv">SV</label>
                {state?.translating?.sv && <LoadingIndicator size="sm" label="Translating" />}
              </Box>
              <MarkdownEditor
                labelId={"label-sv"}
                value={state?.sv ?? ""}
                readOnly={!key}
                onChange={(value) => dispatch(storeTranslation(Language.SV, value))}
              />
            </VBox>
          </VBox>
        }
      />
      {when(state?.addDialog, (addDialog) => (
        <Dialog
          title={"Add translation"}
          onCancel={[update("addDialog", undefined)]}
          isSubmitDisabled={isBlank(addDialog.input)}
          onSubmit={() => {
            const input = trim(addDialog.input, ' "');
            if (containsTranslation(input)) {
              dispatch([update("addDialog", { ...addDialog, input, error: "Already exists" })]);
            } else {
              dispatch([update("addDialog", undefined), ...addTranslation(input)]);
            }
          }}
        >
          <TextInput
            label={"Key"}
            value={addDialog.input}
            onChange={(event) =>
              dispatch([update("addDialog", { ...addDialog, input: event.target.value })])
            }
            message={addDialog.error}
            error={Boolean(addDialog.error)}
          />
        </Dialog>
      ))}
      {when(state?.renameDialog, (renameDialog) => (
        <Dialog
          title={"Rename translation"}
          onCancel={[update("renameDialog", undefined)]}
          isSubmitDisabled={isBlank(renameDialog.input)}
          onSubmit={() => {
            if (containsTranslation(renameDialog.input.trim())) {
              dispatch([update("renameDialog", { ...renameDialog, error: "Already exists" })]);
            } else {
              dispatch([
                update("renameDialog", undefined),
                ...renameTranslation(key, renameDialog.input),
              ]);
            }
          }}
        >
          <TextInput
            label={"Key"}
            value={renameDialog.input}
            onChange={(event) =>
              dispatch([update("renameDialog", { ...renameDialog, input: event.target.value })])
            }
            message={renameDialog.error}
            error={Boolean(renameDialog.error)}
          />
        </Dialog>
      ))}
    </>
  );
});
